import { Recipe } from "libs/types/ai-gateway/src/types/recipe-types";
import { AgeType } from "libs/cat-calculations/src/lib/cat-calculations.types";
import { RecipesForCatResponse } from "@forms/schema";
import { IRecipeItem } from "libs/state/src/lib/stores/signupFlow/types";

import { Allergen } from "@/primary-models";

export const DEFAULT_TRIAL_RECIPE_COUNT = 14;
export const SEVEN_DAY_TRIAL_COUNT = 7;

export type TraySize = 7 | 14 | 28;

type RecipeAllocation = Partial<Record<Recipe, number>>;
type AllergyScenario = "NO_ALLERGY" | "BEEF" | "TURKEY" | "DUCK" | "CHICKEN" | "SALMON" | "CHICKEN_FISH";

/**
 * Recipe allocations organised by tray size, then by allergy scenario.
 *
 * | Allergy Scenario | Excluded Recipes                        | Available Recipes       |
 * |------------------|-----------------------------------------|-------------------------|
 * | None             | -                                       | CLUCK, GOBBLE, QUACK, MOOO |
 * | Pork             | OINK                                    | Fast-path (no effect)   |
 * | Lamb             | BAAA                                    | Fast-path (no effect)   |
 * | Whitefish        | SPLASH                                  | Fast-path (no effect)   |
 * | Beef             | MOOO, BAAA                              | CLUCK, GOBBLE, QUACK    |
 * | Turkey           | GOBBLE, SPLASH                          | CLUCK, QUACK, MOOO      |
 * | Duck             | QUACK                                   | CLUCK, GOBBLE, MOOO     |
 * | Chicken          | CLUCK, GOBBLE, QUACK                    | MOOO, OINK, SPLASH      |
 * | Salmon           | SPLASH, QUACK, MOOO, BAAA, OINK         | CLUCK, GOBBLE           |
 * | Chicken+Fish     | CLUCK, GOBBLE, QUACK, SPLASH            | MOOO only               |
 *
 */
const ALLOCATIONS: Record<TraySize, Record<AllergyScenario, RecipeAllocation>> = {
  7: {
    NO_ALLERGY: { CLUCK: 4, GOBBLE: 2, QUACK: 1, MOOO: 0 },
    BEEF: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
    TURKEY: { CLUCK: 4, QUACK: 2, MOOO: 1 },
    DUCK: { CLUCK: 4, GOBBLE: 2, MOOO: 1 },
    CHICKEN: { OINK: 4, MOOO: 2, SPLASH: 1 },
    SALMON: { CLUCK: 4, GOBBLE: 3 },
    CHICKEN_FISH: { MOOO: 7 },
  },
  14: {
    NO_ALLERGY: { CLUCK: 7, GOBBLE: 3, QUACK: 2, MOOO: 2 },
    BEEF: { CLUCK: 7, GOBBLE: 4, QUACK: 3 },
    TURKEY: { CLUCK: 7, QUACK: 4, MOOO: 3 },
    DUCK: { CLUCK: 7, GOBBLE: 4, MOOO: 3 },
    CHICKEN: { MOOO: 7, OINK: 4, SPLASH: 3 },
    SALMON: { CLUCK: 9, GOBBLE: 5 },
    CHICKEN_FISH: { MOOO: 14 },
  },
  28: {
    NO_ALLERGY: { CLUCK: 14, GOBBLE: 6, QUACK: 4, MOOO: 4 },
    BEEF: { CLUCK: 14, GOBBLE: 8, QUACK: 6 },
    TURKEY: { CLUCK: 14, QUACK: 8, MOOO: 6 },
    DUCK: { CLUCK: 14, GOBBLE: 8, MOOO: 6 },
    CHICKEN: { MOOO: 14, OINK: 8, SPLASH: 6 },
    SALMON: { CLUCK: 5, GOBBLE: 10 },
    CHICKEN_FISH: { MOOO: 28 },
  },
};

/**
 * Default recipe distributions for each tray size (no allergies).
 */
const DEFAULT_RECIPES: Record<TraySize, RecipeAllocation> = {
  7: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
  14: { CLUCK: 7, GOBBLE: 3, MOOO: 2, QUACK: 2 },
  28: { CLUCK: 14, GOBBLE: 6, MOOO: 4, QUACK: 4 },
};

export const getDisallowedRecipes = (lifeStage: AgeType | undefined): Recipe[] => {
  if (lifeStage === "kitten") {
    return [ "BAAA" ];
  }
  return [];
};

export const transformRecipeItemsToRecipeMap = (recipeItems: IRecipeItem[]): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  recipeItems.forEach((item) => {
    recipeMap.set(item.type, item.quantity);
  });

  return recipeMap;
};

export const getDefaultRecipes = (traySize: TraySize = 14): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  const defaults = DEFAULT_RECIPES[traySize];

  Object.entries(defaults).forEach(([ recipe, quantity ]) => {
    if (quantity !== undefined && quantity > 0) {
      recipeMap.set(recipe as Recipe, quantity);
    }
  });

  return recipeMap;
};

export const getFreshTrialRecipes = (
  allergies: Allergen[],
  lifeStage: AgeType,
  recipesItems: IRecipeItem[] | undefined = undefined,
  traySize: TraySize = 14,
): RecipesForCatResponse => {
  const recipes = recipesItems ? transformRecipeItemsToRecipeMap(recipesItems) : getDefaultRecipes(traySize);
  return calculateRecipesForCat(recipes, allergies, getDisallowedRecipes(lifeStage), traySize);
};

export const calculateRecipesForCat = (
  defaultRecipeWeightings: Map<Recipe, number>,
  allergies: Allergen[],
  ignoreRecipes: Recipe[],
  traySize: TraySize = 14,
): RecipesForCatResponse => {
  const recipesNotApplicableForCat = allergies?.map((allergy) => allergyRecipeRecord[allergy]);
  const reduced = recipesNotApplicableForCat?.length > 0
    ? recipesNotApplicableForCat.reduce<Set<Recipe>>(
      (prev, cur) => new Set([ ...cur, ...prev ]),
      new Set<Recipe>(),
    )
    : new Set<Recipe>();
  ignoreRecipes.forEach((ir) => reduced.add(ir));
  const recipeQuantities = calculateRecipeQuantities(
    defaultRecipeWeightings,
    reduced,
    traySize,
  );

  const result = {
    allowedRecipesAndQuantities: recipeQuantities,
    allergicRecipes: reduced,
    ignoreRecipes,
  };

  const recipesMap: { [ recipe: string ]: number } = {};
  [ ...result.allowedRecipesAndQuantities.keys() ].forEach((recipe) => {
    const quantity = result.allowedRecipesAndQuantities.get(recipe) || 0;
    if (quantity > 0) {
      recipesMap[recipe] = quantity;
    }
  });

  const allergyArr = Array.from(result.allergicRecipes);
  return {
    allowedRecipesAndQuantities: recipesMap,
    allergicRecipes: allergyArr,
    notAllowedRecipes: result.ignoreRecipes,
    inDefaultRecipeTest: "control",
  };
};

export const calculateRecipeQuantities = (
  defaultRecipes: Map<Recipe, number>,
  ignoreRecipes: Set<Recipe>,
  traySize: TraySize,
): Map<Recipe, number> => {
  // Fast path: no allergies affecting default recipes
  if (!ignoreRecipes || ignoreRecipes.size === 0 ||
       (!([ ...defaultRecipes.keys() ].some((r) => (defaultRecipes.get(r) || 0) > 0 && ignoreRecipes.has(r))))) {
    const toBeReturned: Map<Recipe, number> = new Map();
    defaultRecipes.forEach((quant, recipe) => {
      if (!ignoreRecipes.has(recipe)) {
        toBeReturned.set(recipe, quant);
      } else {
        toBeReturned.delete(recipe);
      }
    });
    return toBeReturned;
  }

  // Initialise all recipes with zero, then remove ignored ones
  const recipesToReturn = new Map<Recipe, number>();
  recipesToReturn.set("BAAA", 0);
  recipesToReturn.set("CLUCK", 0);
  recipesToReturn.set("GOBBLE", 0);
  recipesToReturn.set("MOOO", 0);
  recipesToReturn.set("OINK", 0);
  recipesToReturn.set("QUACK", 0);
  recipesToReturn.set("SPLASH", 0);
  ignoreRecipes.forEach((toBeRemoved) => {
    recipesToReturn.delete(toBeRemoved);
  });

  // Detect allergy scenario and apply allocation
  const scenario = detectAllergyScenario(ignoreRecipes);
  if (scenario !== "FALLBACK") {
    const allocation = ALLOCATIONS[traySize][scenario];
    Object.entries(allocation).forEach(([ recipe, quantity ]) => {
      if (quantity !== undefined) {
        recipesToReturn.set(recipe as Recipe, quantity);
      }
    });
    return recipesToReturn;
  }

  // Fallback: distribute evenly across available recipes
  const allRecipes: Recipe[] = [ "BAAA", "CLUCK", "GOBBLE", "MOOO", "OINK", "QUACK", "SPLASH" ];
  const availableRecipes = allRecipes.filter((r) => !ignoreRecipes.has(r));
  const totalWeight = availableRecipes.length;

  if (totalWeight === 0) {
    return recipesToReturn;
  }

  let remaining = traySize;
  availableRecipes.forEach((recipe) => {
    const quantityToAdd = Math.floor(traySize / totalWeight);
    recipesToReturn.set(recipe, quantityToAdd);
    remaining -= quantityToAdd;
  });

  if (remaining > 0) {
    // TODO: distribute remainder across recipes
  }

  return recipesToReturn;
};

/**
 * Scenario detection rules: each scenario requires specific recipes to be available.
 * Order matters - first match wins (more specific scenarios should come first).
 */
const SCENARIO_RULES: Array<{ scenario: AllergyScenario; requiredRecipes: Recipe[] }> = [
  { scenario: "NO_ALLERGY", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK", "MOOO" ] },
  { scenario: "BEEF", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK" ] },
  { scenario: "TURKEY", requiredRecipes: [ "CLUCK", "QUACK", "MOOO" ] },
  { scenario: "DUCK", requiredRecipes: [ "CLUCK", "GOBBLE", "MOOO" ] },
  { scenario: "CHICKEN", requiredRecipes: [ "MOOO", "SPLASH", "OINK" ] },
  { scenario: "SALMON", requiredRecipes: [ "CLUCK", "GOBBLE" ] },
  { scenario: "CHICKEN_FISH", requiredRecipes: [ "MOOO" ] },
];

/**
 * Detects which allergy scenario applies based on ignored recipes.
 */
const detectAllergyScenario = (ignoreRecipes: Set<Recipe>): AllergyScenario | "FALLBACK" => {
  const isAvailable = (recipe: Recipe) => !ignoreRecipes.has(recipe);

  for (const rule of SCENARIO_RULES) {
    if (rule.requiredRecipes.every(isAvailable)) {
      return rule.scenario;
    }
  }

  return "FALLBACK";
};

export const allergyRecipeRecord: Record<Allergen, Set<Recipe>> = {
  CHICKEN: new Set([ "CLUCK", "QUACK", "GOBBLE" ]),
  WHITEFISH: new Set([ "SPLASH" ]),
  SALMON: new Set([ "SPLASH", "QUACK", "MOOO", "BAAA", "OINK" ]),
  PORK: new Set([ "OINK" ]),
  BEEF: new Set([ "MOOO", "BAAA" ]),
  TURKEY: new Set([ "GOBBLE", "SPLASH" ]),
  DUCK: new Set([ "QUACK" ]),
  LAMB: new Set([ "BAAA" ]),
  TUNA: new Set(),
};
